import React, { useEffect, useRef, useState } from "react";
import { gsap, prefersReducedMotion } from "../../lib/motion";

const SEEN_KEY = "dpe_preloaded";

/**
 * First-visit intro: golden deer + brand fade in, a counter runs 0→100 with a
 * filling gold bar, then the whole curtain wipes up to reveal the site. Shows
 * once per browser session; skipped entirely under prefers-reduced-motion.
 */
export const Preloader = () => {
  const [done, setDone] = useState(() => {
    if (typeof window === "undefined" || prefersReducedMotion()) return true;
    return sessionStorage.getItem(SEEN_KEY) === "1";
  });
  const rootRef = useRef(null);
  const numRef = useRef(null);
  const barRef = useRef(null);
  const deerRef = useRef(null);

  useEffect(() => {
    if (done) return;
    sessionStorage.setItem(SEEN_KEY, "1");
    document.body.style.overflow = "hidden";
    const counter = { v: 0 };
    const tl = gsap.timeline({
      onComplete: () => {
        document.body.style.overflow = "";
        setDone(true);
      },
    });
    gsap.set([deerRef.current, ".pl-brand"], { opacity: 0, y: 22 });
    tl.to([deerRef.current, ".pl-brand"], { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: "power3.out" });
    tl.to(
      counter,
      {
        v: 100,
        duration: 0.9,
        ease: "power2.inOut",
        onUpdate: () => {
          if (numRef.current) numRef.current.textContent = String(Math.round(counter.v)).padStart(3, "0");
          if (barRef.current) barRef.current.style.transform = `scaleX(${counter.v / 100})`;
        },
      },
      0.15
    );
    tl.to(deerRef.current, { scale: 1.08, duration: 0.4, ease: "power2.out" }, "-=0.3");
    tl.to(".pl-inner", { opacity: 0, y: -14, duration: 0.35, ease: "power2.in" }, "+=0.1");
    tl.to(rootRef.current, { yPercent: -100, duration: 0.8, ease: "expo.inOut" }, "-=0.1");

    return () => {
      tl.kill();
      document.body.style.overflow = "";
    };
  }, [done]);

  if (done) return null;

  return (
    <div ref={rootRef} data-testid="preloader" className="fixed inset-0 z-[100] bg-[#0C1510] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 42%, rgba(201,183,156,.12), transparent 62%)" }} />
      <div className="pl-inner relative flex flex-col items-center px-6">
        <img ref={deerRef} src="/deer-logo.png" alt="Deer Park Estates" className="h-20 w-auto drop-shadow-[0_0_26px_rgba(201,183,156,.4)]" />
        <div className="pl-brand mt-5 text-center">
          <p className="font-display text-3xl text-[#F3EEE3]">Deer Park</p>
          <p className="eyebrow text-[0.55rem] text-[#C9B79C] mt-1.5">Estates · Spruce Grove</p>
        </div>
        <div className="pl-brand mt-10 w-[220px]">
          <div className="h-px w-full bg-[#F3EEE3]/12 overflow-hidden">
            <div ref={barRef} className="h-full w-full bg-gradient-to-r from-[#8F7539] to-[#C9B79C] origin-left" style={{ transform: "scaleX(0)" }} />
          </div>
          <div className="mt-3 flex justify-between items-baseline">
            <span className="eyebrow text-[0.5rem] text-[#F3EEE3]/40">Loading</span>
            <span ref={numRef} className="font-display text-lg text-[#F3EEE3] tabular-nums">000</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preloader;
